import { useQueryClient } from '@tanstack/react-query'
import { Logotype } from '@/components/brand/Logotype'
import { Button } from '@/components/ui/Button'
import { useMaintenance } from './etat'
import { delaiEnMots, FUSEAU_PAR_DEFAUT, heureDuCandidat } from './heures'

/**
 * PENDANT — un écran, pas une erreur — RG-9.11, [DECISIONS §196].
 *
 * **Il répond à trois questions, dans cet ordre**, et l'ordre est le sujet :
 *
 * 1. **ce qui se passe** — le motif, en une phrase ;
 * 2. **quand ça revient** — la seule chose que le candidat veut savoir, et la
 *    seule que `php artisan down` ne sait pas dire ;
 * 3. **ce qu'il ne perd pas** — parce que la première crainte devant un site
 *    fermé n'est pas l'attente, c'est d'avoir travaillé pour rien.
 *
 * **Il n'est rendu que lorsque rien n'a été monté** (RG-9.12). La porte tranche
 * une seule fois, à la première réponse : si la fenêtre s'ouvre ensuite, c'est
 * le bandeau qui parle, et l'épreuve en cours reste à l'écran.
 *
 * **Aucun compte à rebours à la seconde.** Le retard, quand il y en a, est dit
 * plutôt que masqué : une heure de reprise maintenue contre l'horloge du
 * candidat coûte plus de confiance que le retard lui-même.
 */
export function EcranDeMaintenance({
  fuseau = FUSEAU_PAR_DEFAUT,
  onEntreeDeLEquipe,
}: {
  fuseau?: string
  /** Ouvre la coque malgré l'écran — voir `PorteDeMaintenance`. */
  onEntreeDeLEquipe?: (() => void) | undefined
}) {
  const client = useQueryClient()
  const maintenance = useMaintenance()

  const reprise = maintenance.repriseA
  // Le délai vient du crochet, dont l'horloge bat toutes les vingt secondes :
  // un `Date.now()` en plein rendu est une lecture impure que React interdit.
  const dans = maintenance.repriseDansMs

  return (
    <main className="mx-auto flex min-h-full max-w-[600px] flex-col gap-6 px-4 pt-16">
      <header className="flex flex-col gap-3">
        {/* Le logotype COMPLET, comme sur l'écran d'entrée : c'est peut-être la
            première page qu'un visiteur voit, et une page d'erreur anonyme ne
            lui apprendrait même pas où il est tombé. */}
        <h1>
          <Logotype taille={31} />
        </h1>
        <h2 className="text-[23px] font-bold">Haicademy est en maintenance</h2>
      </header>

      {/* 1. Ce qui se passe. */}
      <p className="text-[15px]" style={{ color: 'var(--texte-2)' }}>
        {maintenance.motif ?? 'Une mise à jour est en cours.'}
      </p>

      {/* 2. Quand ça revient — en gras, parce que c'est la ligne qu'on cherche. */}
      <p className="text-[17px] font-bold" role="status">
        {reprise !== null && !maintenance.enRetard && (
          <>
            L’application revient à {heureDuCandidat(reprise, fuseau)}, heure d’Haïti
            {dans !== null && dans > 0 ? ` — dans ${delaiEnMots(dans)}` : ''}.
          </>
        )}
        {reprise !== null && maintenance.enRetard && (
          <>
            La reprise était prévue à {heureDuCandidat(reprise, fuseau)}. Elle a du retard :
            réessaie dans quelques minutes.
          </>
        )}
        {reprise === null && (
          <>L’heure de reprise n’a pas été annoncée. Réessaie dans quelques minutes.</>
        )}
      </p>

      {/* 3. Ce qu'il ne perd pas — et rien de plus que ce qui est vrai. */}
      <p className="text-[15px]" style={{ color: 'var(--texte-2)' }}>
        Ton compte, ton abonnement, ta progression et ta série sont intacts. Toutes les réponses que
        tu as envoyées sont enregistrées, et celles que ton appareil gardait hors connexion
        partiront à la reprise.
      </p>

      <Button
        className="self-start"
        onClick={() => {
          // On réinterroge, on ne recharge PAS la page : un rechargement
          // repaierait le paquet de l'application sur un forfait au mégaoctet,
          // à chaque tape, pour une réponse de deux cents octets.
          void client.invalidateQueries({ queryKey: ['maintenance'] })
        }}
      >
        Réessayer
      </Button>

      {/*
        L'entrée de l'équipe — RG-9.16.

        **Sans elle, l'administrateur est enfermé dehors.** La porte lui montre
        cet écran tant qu'il n'a pas de session ; or c'est la session qui porte
        son rôle. Ce lien ne donne AUCUN droit : il rend la coque, dont le
        formulaire de connexion est la seule chose qui réponde encore. Un
        candidat qui le tape retombe sur des écrans fermés.
      */}
      {onEntreeDeLEquipe !== undefined && (
        <Button variant="ghost" size="dense" className="self-start" onClick={onEntreeDeLEquipe}>
          Entrée de l’équipe
        </Button>
      )}
    </main>
  )
}
